import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';

import { messages as GeneralExportMessages } from '../messages/general-export-messages';
import { WhichRecordOptionsGroups } from './option-groups/which-records-option-group';
import { RecordTypeOptionGroup } from './option-groups/record-type-option-group';
import { FormatTypeOptionsGroup } from './option-groups/format-type-option-group';
import { ExportOptions, ExportOptionsValues } from '../enums/export-options-enum';
import style from '../export-records-modal.module.less';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';

export const ExportOptionSelectionModalView = ({
  closeModal,
  onInitiateExport,
  numberRecordsInView,
  numberRecordsSelected,
  setViewToUpdate,
  onEditFormat,
  onDeleteExportFormat,
  messages,
  exportFormats,
  selectedExportFormatTypeId,
  onFormatTypeChanged,
  primaryRecordsOrLineItems,
  onExportRecordTypeChanged,
  recordExportType,
}) => {
  const [viewRecordsOrSelectedRecords, setViewRecordsOrSelectedRecords] = useState(
    numberRecordsSelected > 0 ? ExportOptions.EXPORT_SELECTED_RECORDS : ExportOptions.EXPORT_ALL_RECORDS_IN_VIEW,
  );
  const onExportWhichRecordsChanged = useCallback(
    (optionSelected) => {
      setViewRecordsOrSelectedRecords(optionSelected);
    },
    [setViewRecordsOrSelectedRecords],
  );

  return (
    <>
      <ModalBody>
        <div className={style['export-records-modal-body']}>
          <WhichRecordOptionsGroups
            messages={messages}
            numberRecordsInView={numberRecordsInView}
            numberRecordsSelected={numberRecordsSelected}
            onExportWhichRecordsChanged={onExportWhichRecordsChanged}
            viewRecordsOrSelectedRecords={viewRecordsOrSelectedRecords}
          />
          <RecordTypeOptionGroup
            messages={messages}
            onExportRecordTypeChanged={onExportRecordTypeChanged}
            primaryRecordsOrLineItems={primaryRecordsOrLineItems}
          />
          <FormatTypeOptionsGroup
            exportFormats={exportFormats}
            onCreateNewFormat={setViewToUpdate}
            onDeleteFormat={onDeleteExportFormat}
            onEditFormat={onEditFormat}
            onFormatTypeChanged={onFormatTypeChanged}
            recordExportType={recordExportType}
            selectedExportFormatTypeId={selectedExportFormatTypeId}
          />
        </div>
      </ModalBody>
      <ModalFooter className={style['export-records-modal-footer']}>
        <div className={style['right-buttons']}>
          <Button onClick={closeModal} theme='tertiary'>
            <FormattedMessage {...GeneralExportMessages.cancel} />
          </Button>
          <Button
            disabled={numberRecordsInView === 0 && numberRecordsSelected === 0}
            onClick={() => onInitiateExport(viewRecordsOrSelectedRecords, primaryRecordsOrLineItems, selectedExportFormatTypeId)}
            theme='primary'
          >
            <FormattedMessage {...GeneralExportMessages.initiateExport} />
          </Button>
        </div>
      </ModalFooter>
    </>
  );
};

ExportOptionSelectionModalView.propTypes = {
  closeModal: PropTypes.func.isRequired,
  numberRecordsInView: PropTypes.number.isRequired,
  numberRecordsSelected: PropTypes.number.isRequired,
  onInitiateExport: PropTypes.func.isRequired,
  exportFormats: PropTypes.array.isRequired,
  setViewToUpdate: PropTypes.func.isRequired,
  onEditFormat: PropTypes.func.isRequired,
  onDeleteExportFormat: PropTypes.func.isRequired,
  messages: PropTypes.shape({
    recordType: MessageDescriptorPropType,
    recordTypeWithLineItem: MessageDescriptorPropType,
  }).isRequired,
  primaryRecordsOrLineItems: PropTypes.oneOf(ExportOptionsValues).isRequired,
  onExportRecordTypeChanged: PropTypes.func.isRequired,
  selectedExportFormatTypeId: PropTypes.string.isRequired,
  onFormatTypeChanged: PropTypes.func.isRequired,
  recordExportType: PropTypes.oneOf(ExportOptionsValues).isRequired,
};
